import { Immutable } from "immer";
import React from "react";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { ProductComparePrintOnlyBlock } from "tsi-common-react/src/components/ProductComparePrintOnlyBlock/index";

import { templates } from "@reactivated";
import type { RichTextBlock as RichTextBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * Block dispatcher map for the ``ProductComparePage`` body assembly (#33577).
 * The ``ProductComparePageBodyContent`` StreamBlock = shared
 * ``ProductComparePageContent`` + sealy ``Toolbox``, so its map is the full
 * ``toolboxComponentsMap`` plus the three extra ``ProductComparePageContent``
 * keys (``server/cms/pages/product_compare.py`` ->
 * ``tsicommon.cms.blocks.product_compare.ProductComparePageContent``):
 *   - ``richtext``            -> Wagtail RichTextBlock (raw rich-text string,
 *                                same as the toolbox ``rich_text`` entry).
 *   - ``print_only_richtext`` -> ProductComparePrintOnlyBlock (takes value).
 *   - ``compare_grid``        -> ProductCompareGridSSR. Plain spread of the
 *                                serialized SSR envelope (null outside a
 *                                request), mirroring the sealy
 *                                ProductCompareGridBlockTemplate.tsx re-export
 *                                / the PDP `productModulesBody` entry -- NO
 *                                extra props (contrast stearns, whose toolbox
 *                                flavor passes ``showFirstAvailablePrice`` +
 *                                ``isMinimal``).
 * All three are pure additions (the toolbox key is ``rich_text`` with an
 * underscore; there is no ``compare_grid`` / ``print_only_richtext`` in the
 * toolbox map), so the spread introduces no accidental overrides.
 */
export const productComparePageComponentsMap = {
    ...toolboxComponentsMap,
    richtext: (block: { value: Immutable<RichTextBlockValue> }) => (
        <RichTextBlock value={block.value} />
    ),
    print_only_richtext: ProductComparePrintOnlyBlock,
    compare_grid: (block: {
        value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
    }) => (block.value ? <ProductCompareGridSSR {...block.value} /> : null),
} as const;

export const SealyProductComparePageBody = (
    props: Immutable<{
        page: templates.SealyProductComparePageTemplate["page"];
    }>,
): React.JSX.Element => {
    // Reproduces the field-level
    // ``tsicommon_cms/blocks/product_compare_page.html`` chrome: the
    // ``<section class="product-compare-page-block">`` wrapper around the stream
    // (inherited via ``ProductComparePageContent.Meta.template``). The legacy
    // ``get_context`` vars are server-render-only; the SSR envelope recomputes
    // its own state (including the ``?tiles=`` ordering), so only the section
    // is load-bearing.
    return (
        <section className="product-compare-page-block">
            <StreamField
                tagName={React.Fragment}
                value={props.page.modules.value}
                components={productComparePageComponentsMap}
            />
        </section>
    );
};
